import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IDatabaseService } from 'src/app/shared/services/contracts/database/database-service';
import { IGenericStorage } from 'src/app/shared/services/contracts/database/generic-storage';
import { DatabaseService } from 'src/app/shared/services/database.service';

import { IStateRegistryPersister } from '../contracts/state/state-registry-persister';
import { Deferred } from '../deferred/deferred';

@Injectable({ providedIn: 'root' })
export class StateRegistryPersister extends Subject<{ key: string; value: any }> implements IStateRegistryPersister {
  private _database: Partial<IGenericStorage>;
  private _ready = new Deferred<void>();

  constructor(@Inject(DatabaseService) private _dbService: IDatabaseService) {
    super();

    // tslint:disable-next-line: no-floating-promises
    (async () => {
      this._database = await this._dbService.getDatabase('statePersister.db');
      this._ready.resolve();
    })();
  }

  public async update(state: string, path: string, models: any): Promise<void> {
    await this._ready.promise;
    await this._database.set(`${state}:${path}`, { _id: `${state}:${path}`, models });
  }

  public async rehydrate(): Promise<void> {
    const items = {};
    await this._ready.promise;

    await this._database.forEach((models, key) => {
      if (!key) {
        window.logger.error(
          `[StateRegistryPersister] rehydrate: #23119 key is undefined or null for model: ${JSON.stringify(models)}}`,
          new Error('Missing key in rehydrate')
        );
        return;
      }
      // skip sentry throttle entries - they are not state entries
      // Entries created by SentryThrottleService start with 'sentry:'
      if (key.startsWith('sentry:')) {
        return;
      }
      // clear all entities with key :items during rehydrate to avoid uncontrolled state growth
      // dont clear patientAppUsers as they are only written to state once (during websync)
      if (key.endsWith(':items') && !key.includes('patientAppUser')) {
        this._database.set(key, { _id: key, models: [] });
      } else if (key.endsWith(':selectedItem')) {
        // Do not rehydrate selectedItem - it is tab-specific state and causes cross-tab
        // contamination when multiple tabs have different items selected (e.g. on Windows/Edge
        // where IndexedDB rehydration arrives after the subscription is set up, overwriting
        // the correct item loaded from the URL parameter with a stale item from another tab).
        this._database.set(key, { _id: key, models: null });
      } else {
        // Write everything else from state persister into state
        // Every custom keys like :dashboard, etc. must be handled and cleared on their own!
        items[key] = models;
      }
    });

    for (const key in items) {
      if (items.hasOwnProperty(key)) {
        this.next({ key, value: items[key].models });
      }
    }
    this.complete();
  }
}
